import { createContext, FC, ReactNode, useCallback, useState } from 'react';

import * as Ably from 'ably';
import { useChannel } from 'ably/react';
import { AxiosResponse } from 'axios';

import { KaseApi, KasePropertyApi } from '@/api';
import { Loader } from '@/components';
import { KaseRoleBasedAction } from '@/constants';
import { useFormConfigLoader } from '@/hooks';
import { IKase } from '@/models';
import { isReadOnlyKaseRole } from '@/services';
import { useToastStore, useUserStore } from '@/store';

interface KaseContextValue {
  kase: IKase | null;
  fetchKase: (kaseId: string) => Promise<IKase | null>;
  updateKase: (data: IKase | null) => void;
  isReadOnlyNames: boolean;
  isReadOnlyVehicles: boolean;
  isReadOnlyOffenses: boolean;
  isReadOnlyProperties: boolean;
  isReadOnlyArrestees: boolean;
  isReadOnlyNarratives: boolean;
  isReadOnlyFiles: boolean;
  isReadOnlyNibrs: boolean;
  editingSection?: string;
  setEditingSection: (sectionName: string) => void;
  itemNIBRSFetchingMethods?: Map<string, any>;
  setIsLoading: (isLoading: boolean) => void;
  validationRules: any;
  dataRefreshKey: number;
  triggerDataRefresh: () => void;
}

interface KaseProviderProps {
  children: ReactNode;
  initialKase?: IKase | null;
}

export const DEFAULT_KASE_CONTEXT_VALUE: KaseContextValue = {
  kase: null,
  fetchKase: async () => null,
  updateKase: () => {},
  isReadOnlyNames: false,
  isReadOnlyVehicles: false,
  isReadOnlyOffenses: false,
  isReadOnlyProperties: false,
  isReadOnlyArrestees: false,
  isReadOnlyNarratives: false,
  isReadOnlyFiles: false,
  isReadOnlyNibrs: false,
  setEditingSection: () => {},
  setIsLoading: () => {},
  validationRules: [],
  dataRefreshKey: 0,
  triggerDataRefresh: () => {},
};

export const KaseContext = createContext<KaseContextValue>(
  DEFAULT_KASE_CONTEXT_VALUE,
);

export const KaseProvider: FC<KaseProviderProps> = ({
  children,
  initialKase = null,
}) => {
  const { updateToast } = useToastStore();
  const { user, account } = useUserStore();
  const [kase, setKase] = useState<IKase | null>(initialKase);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [itemNIBRSFetchingMethods, setNIBRSItemsFetchingMethods] =
    useState<Map<string, Promise<AxiosResponse<any>>>>();

  const [editingSection, setEditingSection] = useState<string>();
  const [dataRefreshKey, setDataRefreshKey] = useState(0);
  const triggerDataRefresh = useCallback(() => {
    setDataRefreshKey((prev) => prev + 1);
  }, []);
  const isReadOnlyNames = isReadOnlyKaseRole(
    KaseRoleBasedAction.NAMES,
    user,
    kase,
  );
  const isReadOnlyVehicles = isReadOnlyKaseRole(
    KaseRoleBasedAction.VEHICLES,
    user,
    kase,
  );
  const isReadOnlyOffenses = isReadOnlyKaseRole(
    KaseRoleBasedAction.CRIMES,
    user,
    kase,
  );
  const isReadOnlyProperties = isReadOnlyKaseRole(
    KaseRoleBasedAction.PROPERTIES,
    user,
    kase,
  );
  const isReadOnlyArrestees = isReadOnlyKaseRole(
    KaseRoleBasedAction.ARRESTEES,
    user,
    kase,
  );
  const isReadOnlyNarratives = isReadOnlyKaseRole(
    KaseRoleBasedAction.NARRATIVE_MAIN,
    user,
    kase,
  );
  const isReadOnlyFiles = isReadOnlyKaseRole(
    KaseRoleBasedAction.FILES,
    user,
    kase,
  );
  const isReadOnlyNibrs = isReadOnlyKaseRole(
    KaseRoleBasedAction.NIBRS,
    user,
    kase,
  );

  const { allOptions: validationRules } = useFormConfigLoader({
    optionsName: 'NIBRS_VALIDATION',
  });

  const fetchKase = async (kaseId: string) => {
    setIsLoading(true);
    try {
      const res = await KaseApi.getOne(kaseId);
      setKase(res.data);

      setNIBRSItemsFetchingMethods(
        new Map<string, any>([
          ['INCIDENT', KaseApi.getOne],
          ['PROPERTY', KasePropertyApi.list],
          ['ARRESTEE', KaseApi.getArrestees],
        ]),
      );
      return res.data;
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const updateKase = (data: IKase | null) => {
    setKase(data);
  };

  // Listen to parent CFS channel for incident code updates
  // When CFS incident codes are updated, the backend syncs them to the case
  // This listener refetches the case to get the updated incident codes
  const cfsParentId = kase?.cfsParent;
  const shouldSubscribeToCfs = !!cfsParentId && !!account?._id && !!kase?._id;

  useChannel(
    shouldSubscribeToCfs ? `account:${account._id}:cfs:${cfsParentId}` : '',
    useCallback(
      async (message: Ably.Types.Message) => {
        if (!shouldSubscribeToCfs || !kase?._id) return;

        const messageData = message.data;

        // When parent CFS incident codes are updated, refetch the case
        // The backend has already synced the incident codes
        if (messageData?.incidentCodes) {
          try {
            const res = await KaseApi.getOne(kase._id);
            setKase(res.data);
          } catch {
            // swallow — sync refetch is best-effort, user can trigger manually
          }
        }
      },
      [shouldSubscribeToCfs, kase?._id],
    ),
  );

  return (
    <KaseContext.Provider
      value={{
        kase,
        fetchKase,
        updateKase,
        isReadOnlyNames,
        isReadOnlyVehicles,
        isReadOnlyOffenses,
        isReadOnlyProperties,
        isReadOnlyArrestees,
        isReadOnlyNarratives,
        isReadOnlyFiles,
        isReadOnlyNibrs,
        editingSection,
        setEditingSection,
        itemNIBRSFetchingMethods,
        setIsLoading,
        validationRules,
        dataRefreshKey,
        triggerDataRefresh,
      }}
    >
      {children}
      <Loader open={isLoading} />
    </KaseContext.Provider>
  );
};
